import { z } from 'zod';
import { LIMITS } from '../registries/limits.ts';
import { ACCOUNT_ROLES } from '../registries/roles.ts';
import { DisplayName, Id, IsoDateTime } from './primitives.ts';

export const AccountRoleSchema = z.enum(ACCOUNT_ROLES);

export const Login = z
  .string()
  .trim()
  .toLowerCase()
  .min(LIMITS.login.min)
  .max(LIMITS.login.max)
  .regex(LIMITS.login.pattern);

export const Password = z.string().min(LIMITS.password.min).max(LIMITS.password.max);

export const AccountPublic = z.object({
  id: Id,
  login: z.string(),
  displayName: z.string(),
  role: AccountRoleSchema,
  createdAt: IsoDateTime,
  disabledAt: IsoDateTime.nullable(),
});
export type AccountPublic = z.infer<typeof AccountPublic>;

/** A browser-level guest identity; the same guest can attend several screenings. */
export const GuestPublic = z.object({
  id: Id,
  displayName: z.string(),
});
export type GuestPublic = z.infer<typeof GuestPublic>;

export const SessionView = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('anonymous') }),
  z.object({ kind: z.literal('account'), account: AccountPublic }),
  z.object({ kind: z.literal('guest'), guest: GuestPublic }),
]);
export type SessionView = z.infer<typeof SessionView>;

export const LoginBody = z.object({ login: Login, password: z.string().min(1).max(LIMITS.password.max) });

export const AccountInvitationPreview = z.object({ role: AccountRoleSchema, expiresAt: IsoDateTime });

export const AcceptAccountInvitationBody = z.object({
  login: Login,
  displayName: DisplayName,
  password: Password,
});

export const CreateAccountInvitationBody = z.object({ role: AccountRoleSchema });

export const AccountInvitationCreated = z.object({
  /** Absolute one-time link the owner sends to the new host. */
  url: z.url(),
  expiresAt: IsoDateTime,
});

export const UpdateAccountBody = z.object({ disabled: z.boolean() });

/** What a signed-in host may change about their own account. */
export const UpdateMyAccountBody = z.object({ displayName: DisplayName });
